import { Skeleton } from "@/components/ui/skeleton";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Table, TableBody, TableCell, TableRow } from "@/components/ui/table";

interface StatementSkeletonProps {
  type?: "balance-sheet" | "profit-loss";
}

export function StatementSkeleton({
  type = "balance-sheet",
}: StatementSkeletonProps) {
  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="space-y-2">
            <Skeleton className="h-6 w-64 animate-shimmer" />
            <Skeleton className="h-4 w-40 animate-shimmer" />
          </div>
          <div className="flex gap-2">
            <Skeleton className="h-9 w-24 animate-shimmer" />
            <Skeleton className="h-9 w-24 animate-shimmer" />
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-6">
          {type === "balance-sheet" ? (
            <>
              {/* Assets Section */}
              <div>
                <Skeleton className="h-6 w-24 mb-4 animate-shimmer" />

                {/* Current Assets */}
                <div className="mb-4">
                  <Skeleton className="h-5 w-32 mb-2 animate-shimmer" />
                  <Table>
                    <TableBody>
                      {[...Array(4)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell className="pl-8">
                            <Skeleton className="h-4 w-48 animate-shimmer" />
                          </TableCell>
                          <TableCell className="text-right">
                            <Skeleton className="h-4 w-24 ml-auto animate-shimmer" />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50">
                        <TableCell className="pl-4">
                          <Skeleton className="h-4 w-40 animate-shimmer" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-4 w-28 ml-auto animate-shimmer" />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Fixed Assets */}
                <div className="mb-4">
                  <Skeleton className="h-5 w-28 mb-2 animate-shimmer" />
                  <Table>
                    <TableBody>
                      {[...Array(3)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell className="pl-8">
                            <Skeleton className="h-4 w-44 animate-shimmer" />
                          </TableCell>
                          <TableCell className="text-right">
                            <Skeleton className="h-4 w-24 ml-auto animate-shimmer" />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50">
                        <TableCell className="pl-4">
                          <Skeleton className="h-4 w-36 animate-shimmer" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-4 w-28 ml-auto animate-shimmer" />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Total Assets */}
                <Table>
                  <TableBody>
                    <TableRow className="bg-primary/10">
                      <TableCell>
                        <Skeleton className="h-5 w-32 animate-shimmer" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-5 w-32 ml-auto animate-shimmer" />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Liabilities & Equity Section */}
              <div>
                <Skeleton className="h-6 w-48 mb-4 animate-shimmer" />

                {/* Current Liabilities */}
                <div className="mb-4">
                  <Skeleton className="h-5 w-40 mb-2 animate-shimmer" />
                  <Table>
                    <TableBody>
                      {[...Array(3)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell className="pl-8">
                            <Skeleton className="h-4 w-40 animate-shimmer" />
                          </TableCell>
                          <TableCell className="text-right">
                            <Skeleton className="h-4 w-24 ml-auto animate-shimmer" />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50">
                        <TableCell className="pl-4">
                          <Skeleton className="h-4 w-48 animate-shimmer" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-4 w-28 ml-auto animate-shimmer" />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>

                {/* Equity */}
                <div className="mb-4">
                  <Skeleton className="h-5 w-20 mb-2 animate-shimmer" />
                  <Table>
                    <TableBody>
                      {[...Array(3)].map((_, i) => (
                        <TableRow key={i}>
                          <TableCell className="pl-8">
                            <Skeleton className="h-4 w-36 animate-shimmer" />
                          </TableCell>
                          <TableCell className="text-right">
                            <Skeleton className="h-4 w-24 ml-auto animate-shimmer" />
                          </TableCell>
                        </TableRow>
                      ))}
                      <TableRow className="bg-muted/50">
                        <TableCell className="pl-4">
                          <Skeleton className="h-4 w-32 animate-shimmer" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-4 w-28 ml-auto animate-shimmer" />
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </div>
              </div>
            </>
          ) : (
            <>
              {/* Revenue Section */}
              <div>
                <Skeleton className="h-6 w-24 mb-4 animate-shimmer" />
                <Table>
                  <TableBody>
                    {[...Array(3)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell className="pl-8">
                          <Skeleton className="h-4 w-40 animate-shimmer" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-4 w-24 ml-auto animate-shimmer" />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/50">
                      <TableCell className="pl-4">
                        <Skeleton className="h-4 w-32 animate-shimmer" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-4 w-28 ml-auto animate-shimmer" />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Expenses Section */}
              <div>
                <Skeleton className="h-6 w-28 mb-4 animate-shimmer" />
                <Table>
                  <TableBody>
                    {[...Array(5)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell className="pl-8">
                          <Skeleton className="h-4 w-44 animate-shimmer" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-4 w-24 ml-auto animate-shimmer" />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-muted/50">
                      <TableCell className="pl-4">
                        <Skeleton className="h-4 w-36 animate-shimmer" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-4 w-28 ml-auto animate-shimmer" />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>

              {/* Results Section */}
              <div>
                <Skeleton className="h-6 w-24 mb-4 animate-shimmer" />
                <Table>
                  <TableBody>
                    {[...Array(2)].map((_, i) => (
                      <TableRow key={i}>
                        <TableCell>
                          <Skeleton className="h-4 w-36 animate-shimmer" />
                        </TableCell>
                        <TableCell className="text-right">
                          <Skeleton className="h-4 w-28 ml-auto animate-shimmer" />
                        </TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-primary/10">
                      <TableCell>
                        <Skeleton className="h-5 w-28 animate-shimmer" />
                      </TableCell>
                      <TableCell className="text-right">
                        <Skeleton className="h-5 w-32 ml-auto animate-shimmer" />
                      </TableCell>
                    </TableRow>
                  </TableBody>
                </Table>
              </div>
            </>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
